// Pure helpers turning a staff member's schedule (trpc.staff.getSchedule —
// working hours + recurring breaks + time off) into the day grid's "no staff
// working these hours" overlay. Deliberately mirrors
// apps/mobile/src/business/staff-availability.ts's `blockedBandsForDay` +
// `timeOffBandsForDay` — same duplication convention as this file's own
// STATUS_STRIPE map (see page.tsx), since the two apps don't share a
// framework-free calendar package yet. Unlike the mobile version, the web
// day grid already has one column per staff member (no "all staff" merge
// needed) and has no separate time-off stamp UI, so time off is folded
// directly into the blocked bands here instead of getting its own labeled
// stamp.

export type WorkingHoursEntry = { dayOfWeek: number; startTime: string; endTime: string };
export type BreakEntry = { dayOfWeek: number; startTime: string; endTime: string };
export type TimeOffEntry = { startsAt: string; endsAt: string };
export type DayBoundsUtc = { from: Date; to: Date };
export type BlockedBand = { startMin: number; endMin: number };

export function hhmmToMin(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h! * 60 + m!;
}

// Merge overlapping/adjacent bands (sorted ascending) — a day-off band that
// happens to overlap a break, or two time-off entries that touch, should
// render as one continuous hatch, not a visually-doubled stack.
function mergeBands(bands: BlockedBand[]): BlockedBand[] {
  if (bands.length <= 1) return bands;
  const sorted = [...bands].sort((a, b) => a.startMin - b.startMin);
  const merged: BlockedBand[] = [{ ...sorted[0]! }];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1]!;
    const cur = sorted[i]!;
    if (cur.startMin <= last.endMin) last.endMin = Math.max(last.endMin, cur.endMin);
    else merged.push({ ...cur });
  }
  return merged;
}

/**
 * Minute-of-day bands (clipped to [gridStartMin, gridEndMin]) where this
 * staff member is not working: outside their nominal working hours (or the
 * whole day, if they have no working-hours row for this weekday), a
 * recurring break, or a time-off entry overlapping the viewed day.
 */
export function blockedBandsForStaffDay(params: {
  dayOfWeek: number;
  workingHours: WorkingHoursEntry[];
  breaks: BreakEntry[];
  timeOff: TimeOffEntry[];
  dayBounds: DayBoundsUtc;
  gridStartMin: number;
  gridEndMin: number;
}): BlockedBand[] {
  const { dayOfWeek, workingHours, breaks, timeOff, dayBounds, gridStartMin, gridEndMin } = params;
  const bands: BlockedBand[] = [];

  const today = workingHours.find((wh) => wh.dayOfWeek === dayOfWeek);
  if (!today) {
    bands.push({ startMin: gridStartMin, endMin: gridEndMin });
  } else {
    const workStart = hhmmToMin(today.startTime);
    const workEnd = hhmmToMin(today.endTime);
    if (workStart > gridStartMin) {
      bands.push({ startMin: gridStartMin, endMin: Math.min(workStart, gridEndMin) });
    }
    if (workEnd < gridEndMin) {
      bands.push({ startMin: Math.max(workEnd, gridStartMin), endMin: gridEndMin });
    }
    for (const br of breaks) {
      if (br.dayOfWeek !== dayOfWeek) continue;
      const start = Math.max(hhmmToMin(br.startTime), gridStartMin);
      const end = Math.min(hhmmToMin(br.endTime), gridEndMin);
      if (start < end) bands.push({ startMin: start, endMin: end });
    }
  }

  const fromMs = dayBounds.from.getTime();
  const toMs = dayBounds.to.getTime();
  for (const to of timeOff) {
    const overlapStart = Math.max(new Date(to.startsAt).getTime(), fromMs);
    const overlapEnd = Math.min(new Date(to.endsAt).getTime(), toMs);
    if (overlapStart >= overlapEnd) continue;
    const startMin = Math.max(gridStartMin, Math.round((overlapStart - fromMs) / 60_000));
    const endMin = Math.min(gridEndMin, Math.round((overlapEnd - fromMs) / 60_000));
    if (startMin < endMin) bands.push({ startMin, endMin });
  }

  return mergeBands(bands.filter((b) => b.startMin < b.endMin));
}

/** True when the merged blocked bands cover the entire visible grid — the
 * "not working at all today" case, worth a small centered label since an
 * otherwise-empty hatched column with no appointments and no shift line
 * could read as a loading/error state rather than an intentional day off. */
export function isFullyBlocked(
  bands: BlockedBand[],
  gridStartMin: number,
  gridEndMin: number,
): boolean {
  return (
    bands.length === 1 && bands[0]!.startMin === gridStartMin && bands[0]!.endMin === gridEndMin
  );
}
